// SCR-STAFF-406 (بخش عضویت؛ US-BIL-075): عضویت‌های مراجعه‌کننده با وضعیت، دوره جاری، مزایا (تخفیف، جلسات رایگان باقیمانده، نوبت‌دهی اولویت‌دار)،
// تمدید خودکار و تاریخچه دوره‌ها؛ فروش عضویت از طرح‌های منتشرشده CAT (صورتحساب عضویت ← پرداخت در صندوق؛ طرح رایگان بی‌درنگ فعال).
import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { unwrap, type BilMembershipView } from '@phoenix/api-client';
import { useAccess, useIdempotencyKey, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { ErrorState, faDigits, FormDialog, JalaliDate } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link as RouterLink } from 'react-router';
import { Amount, bilKeys, cashierPatientPath, MembershipStatusChip } from './common';

const percent = (basisPoints: number | string) => faDigits((Number(basisPoints) / 100).toLocaleString('en-US'));

export function MembershipsPanel({ patientId }: { patientId: string }) {
  const { t } = useTranslation('bil');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const queryClient = useQueryClient();
  const key = useIdempotencyKey();
  const [selling, setSelling] = useState(false);
  const [planId, setPlanId] = useState('');
  const canSell = access.accessMode === 'Full' && access.can('bil.membership.sell');
  const memberships = useQuery({
    queryKey: bilKeys.memberships(session?.tenantId, patientId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/billing/memberships', { params: { query: { patientId } } })),
  });
  const plans = useQuery({
    queryKey: bilKeys.membershipPlans(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/billing/membership-plans')),
    enabled: selling,
  });
  const refresh = () => void queryClient.invalidateQueries({ queryKey: bilKeys.all(session?.tenantId) });
  const sell = useMutation({
    mutationFn: () => unwrap(tenantApi.POST('/api/v1/billing/memberships', { body: { patientId, planId }, headers: { 'Idempotency-Key': key.current() } })),
    onSettled: (_data, error) => key.settle(error ?? undefined),
    onSuccess: () => {
      setSelling(false);
      setPlanId('');
      refresh();
    },
  });
  const autoRenew = useMutation({
    mutationFn: ({ membership, value }: { membership: BilMembershipView; value: boolean }) =>
      unwrap(
        tenantApi.POST('/api/v1/billing/memberships/{id}:auto-renew', {
          params: { path: { id: membership.id } },
          body: { autoRenew: value },
          headers: { 'Idempotency-Key': key.current() },
        }),
      ),
    onSettled: (_data, error) => key.settle(error ?? undefined),
    onSuccess: refresh,
  });
  const items = memberships.data?.items ?? [];
  const selectedPlan = plans.data?.items.find((plan) => plan.planId === planId);
  return (
    <Paper variant="outlined" sx={{ p: 2 }} data-testid="bil-memberships">
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="subtitle1">{t('bil:memberships.title')}</Typography>
        {canSell ? (
          <Button size="small" variant="outlined" onClick={() => setSelling(true)} data-testid="bil-membership-sell">
            {t('bil:memberships.sell')}
          </Button>
        ) : null}
      </Stack>
      {memberships.error ? <ErrorState error={memberships.error} compact onRetry={() => void memberships.refetch()} /> : null}
      {items.length === 0 ? (
        <Typography color="text.secondary">{t('bil:memberships.none')}</Typography>
      ) : (
        <Table size="small" aria-label={t('bil:memberships.title')}>
          <TableHead>
            <TableRow>
              <TableCell>{t('bil:memberships.plan')}</TableCell>
              <TableCell>{t('bil:deposits.columns.status')}</TableCell>
              <TableCell>{t('bil:memberships.period_end')}</TableCell>
              <TableCell>{t('bil:memberships.benefits')}</TableCell>
              <TableCell>{t('bil:cashier.amount')}</TableCell>
              <TableCell>{t('bil:memberships.auto_renew')}</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((membership) => {
              const freeLeft = membership.credits.filter((credit) => credit.status === 'Available').length;
              return (
                <TableRow key={membership.id} data-testid="bil-membership-row" data-status={membership.status}>
                  <TableCell>{membership.planName}</TableCell>
                  <TableCell>
                    <MembershipStatusChip status={membership.status} />
                  </TableCell>
                  <TableCell>{membership.currentPeriodEnd ? <JalaliDate value={membership.currentPeriodEnd} /> : '—'}</TableCell>
                  <TableCell>
                    {[
                      Number(membership.discountBasisPoints) > 0 ? t('bil:memberships.discount', { percent: percent(membership.discountBasisPoints) }) : null,
                      freeLeft > 0 ? t('bil:memberships.free_left', { n: faDigits(String(freeLeft)) }) : null,
                      membership.priorityBooking ? t('bil:memberships.priority') : null,
                    ]
                      .filter(Boolean)
                      .join(' · ') || '—'}
                  </TableCell>
                  <TableCell>
                    <Amount money={membership.price} />
                  </TableCell>
                  <TableCell>
                    <Switch
                      size="small"
                      checked={membership.autoRenew}
                      disabled={!canSell || membership.status === 'Cancelled' || membership.status === 'Lapsed' || autoRenew.isPending}
                      onChange={(event) => autoRenew.mutate({ membership, value: event.target.checked })}
                      slotProps={{ input: { 'aria-label': t('bil:memberships.auto_renew') } }}
                    />
                  </TableCell>
                  <TableCell>
                    {membership.status === 'PendingPayment' && access.can('bil.payment.receive') ? (
                      <Button size="small" component={RouterLink} to={cashierPatientPath(patientId)} data-testid="bil-membership-pay">
                        {t('bil:installments.pay')}
                      </Button>
                    ) : null}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
      {autoRenew.error ? <ErrorState error={autoRenew.error} compact /> : null}
      <FormDialog
        open={selling}
        titleKey="bil:memberships.sell"
        onSubmit={() => planId !== '' && sell.mutate()}
        onClose={() => setSelling(false)}
        isDirty={planId !== ''}
        isSubmitting={sell.isPending}
        submitError={sell.error}
      >
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField select required label={t('bil:memberships.plan')} value={planId} onChange={(event) => setPlanId(event.target.value)} slotProps={{ htmlInput: { 'data-testid': 'bil-membership-plan' } }}>
            {(plans.data?.items ?? []).map((plan) => (
              <MenuItem key={plan.planId} value={plan.planId}>
                {plan.name} — {t('bil:memberships.months', { n: faDigits(String(plan.periodMonths)) })}
              </MenuItem>
            ))}
          </TextField>
          {selectedPlan ? (
            <Typography variant="body2" data-testid="bil-membership-plan-summary">
              <Amount money={selectedPlan.price} />
              {Number(selectedPlan.discountBasisPoints) > 0 ? ` · ${t('bil:memberships.discount', { percent: percent(selectedPlan.discountBasisPoints) })}` : ''}
              {Number(selectedPlan.freeSessions) > 0 ? ` · ${t('bil:memberships.free_sessions', { n: faDigits(String(selectedPlan.freeSessions)) })}` : ''}
              {selectedPlan.carryOver ? ` · ${t('bil:memberships.carry_over')}` : ''}
              {selectedPlan.priorityBooking ? ` · ${t('bil:memberships.priority')}` : ''}
            </Typography>
          ) : null}
          <FormControlLabel control={<Switch checked disabled />} label={t('bil:memberships.auto_renew_default')} />
        </Stack>
      </FormDialog>
    </Paper>
  );
}
